"use client";

import { get } from "@/utils/utils";
import { IChartApi, ISeriesApi } from "lightweight-charts";
import React, { useEffect, useState } from "react";
import { useChart } from "./context";

export interface PriceLineOverlayProps {
    series: ISeriesApi<"Area"> | null;
    avgPrice: number;
    lastPrice?: number;
    label?: string;
    color?: string;
}

export function PriceLineOverlay( props: PriceLineOverlayProps ) {
    const chartContext = useChart();
    const chart: IChartApi = get( chartContext, "chart", null );
    const series = get( props, "series", null );
    const avgPrice = get( props, "avgPrice", 0 );
    const lastPrice = get( props, "lastPrice", undefined );
    const baseLabel = get( props, "label" );
    const lineColor = get( props, "color", "#9C9C9C" );

    const [ lineState, setLineState ] = useState<
        {
            placement: "inside" | "top" | "bottom";
            topPx: number;
            label: string;
        } | null
    >( null );

    useEffect( () => {
        if ( !series || !avgPrice || !chart ) {
            setLineState( null );
            return;
        }

        const updateLinePosition = () => {
            const priceRange = chart.priceScale( "right" ).getVisibleRange();

            // 没有数据时不显示
            if ( !priceRange ) {
                setLineState( null );
                return;
            }

            const coordinate = series.priceToCoordinate( avgPrice );
            const bottomCoord = series.priceToCoordinate( priceRange.from );
            const topCoord = series.priceToCoordinate( priceRange.to );

            // 开盘价超出可视范围时，根据当前价格判断显示位置
            if ( coordinate === null ) {
                if ( lastPrice !== undefined ) {
                    if ( lastPrice > avgPrice ) {
                        // 当前价高于开盘价，开盘价在下方
                        setLineState( { placement: "inside", topPx: bottomCoord ?? 0, label: `${baseLabel}` } );
                        return;
                    }
                    if ( lastPrice < avgPrice ) {
                        // 当前价低于开盘价，开盘价在上方
                        setLineState( { placement: "inside", topPx: topCoord ?? 0, label: `${baseLabel}` } );
                        return;
                    }
                }
                // 无法判断时不显示
                setLineState( null );
                return;
            }

            // topCoord 和 bottomCoord 都存在时才计算范围
            let minCoordinate = topCoord ?? 0;
            let maxCoordinate = bottomCoord ?? 0;
            if ( minCoordinate > maxCoordinate ) {
                [ minCoordinate, maxCoordinate ] = [ maxCoordinate, minCoordinate ];
            }

            const clampedCoordinate = coordinate < minCoordinate
                ? minCoordinate
                : coordinate > maxCoordinate
                ? maxCoordinate
                : coordinate;

            setLineState( {
                placement: "inside",
                topPx: clampedCoordinate,
                label: baseLabel,
            } );
        };

        // 监听时间轴可视范围变化（滚动、缩放等）
        chart.timeScale().subscribeVisibleLogicalRangeChange( updateLinePosition );
        return () => {
            chart.timeScale().unsubscribeVisibleLogicalRangeChange( updateLinePosition );
        };
    }, [ avgPrice, baseLabel, chart, lastPrice, series ] );

    if ( !lineState ) return null;

    const placement = get( lineState, "placement", "inside" );
    const topPx = get( lineState, "topPx", 0 );
    const label = get( lineState, "label", "" );

    const positionStyle: React.CSSProperties = {};

    if ( placement === "inside" ) {
        positionStyle.transform = `translateY(${topPx}px)`;
    }
    if ( placement === "top" ) {
        positionStyle.transform = `translateY(0)`;
    }
    if ( placement === "bottom" ) {
        positionStyle.transform = `translateY(-100%)`;
    }

    return (
        <div className="absolute left-0 right-0 top-0 z-10 pointer-events-none transition-all" style={positionStyle}>
            <div
                className="w-full h-px"
                style={{
                    backgroundImage: `repeating-linear-gradient(to right, ${lineColor} 0 4px, transparent 4px 8px)`,
                }}
            />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-[77px] h-[23px] px-[4px] text-xs font-medium rounded-[4px] bg-content-subtle text-content-fixed-inverse">
                {label}
            </div>
        </div>
    );
}
